import { resolvePerformerItemIds } from "./performerActionInput";

/**
 * Resolve live performer progress steps for one action item row.
 *
 * @example
 * // Input
 * resolvePerformerProgressForItem(
 *   { action_item: "Email Bob", workflow_id: "wf-1", key: "k1", file_id: "f1" },
 *   { "wf-1::k1::f1": { steps: [{ label: "Drafting email", status: "running" }] } }
 * );
 * // Output
 * { steps: [{ label: "Drafting email", status: "running" }] }
 */
export const resolvePerformerProgressForItem = (item, progressByItem = {}, contextWorkflowId = "") => {
  if (!progressByItem || typeof progressByItem !== "object") {
    return { steps: [] };
  }
  const { stableId, actionText } = resolvePerformerItemIds(item, contextWorkflowId);
  if (stableId && progressByItem[stableId]) {
    return progressByItem[stableId];
  }
  if (actionText && progressByItem[actionText]) {
    return progressByItem[actionText];
  }
  return { steps: [] };
};

/**
 * Merge poll progress payloads into a lookup map keyed by client item id.
 *
 * @example
 * // Input
 * mergePerformerProgressMaps({}, { "wf::k::f": { steps: [{ label: "Gathering context" }] } });
 * // Output
 * { "wf::k::f": { steps: [{ label: "Gathering context" }] } }
 */
export const mergePerformerProgressMaps = (prev, nextProgress) => {
  if (!nextProgress || typeof nextProgress !== "object") {
    return prev || {};
  }
  const merged = { ...(prev || {}) };
  Object.entries(nextProgress).forEach(([key, value]) => {
    if (!key || !value || typeof value !== "object") return;
    merged[key] = value;
  });
  return merged;
};

/**
 * Pick the best available progress payload for one row or details panel.
 *
 * @example
 * // Input
 * resolveItemActiveAiProgress({
 *   item: { workflow_id: "w", key: "k", file_id: "f" },
 *   bulkProgressByItem: { "w::k::f": { steps: [{ label: "Reading emails", status: "running" }] } },
 *   singleRunProgress: { steps: [{ label: "Drafting", status: "running" }] },
 * });
 * // Output
 * { steps: [{ label: "Drafting", status: "running" }] }
 */
export const resolveItemActiveAiProgress = ({
  item,
  bulkProgressByItem = {},
  singleRunProgress = null,
  contextWorkflowId = "",
}) => {
  if (singleRunProgress?.steps?.length) {
    return singleRunProgress;
  }
  return resolvePerformerProgressForItem(item, bulkProgressByItem, contextWorkflowId);
};

/**
 * Pick the primary performer row for one action item, skipping CRM side-effects.
 *
 * @example
 * // Input
 * pickPrimaryPerformerResult(
 *   [
 *     { client_item_id: "wf::k::f", draft: "Hi Jane" },
 *     { client_item_id: "wf::k::f:crm", crm_side_effect: true },
 *   ],
 *   "wf::k::f"
 * );
 * // Output
 * { client_item_id: "wf::k::f", draft: "Hi Jane" }
 */
export const pickPrimaryPerformerResult = (results, stableId = "") => {
  const rows = Array.isArray(results) ? results.filter((row) => row && typeof row === "object") : [];
  if (rows.length === 0) return null;
  const wantedId = String(stableId || "").trim();
  const primaryRows = rows.filter((row) => !row.crm_side_effect);
  if (wantedId) {
    const matched = primaryRows.find(
      (row) => String(row.client_item_id || "").trim() === wantedId
    );
    if (matched) return matched;
  }
  return primaryRows[0] || rows[0] || null;
};

/**
 * Pick the CRM side-effect row proposed alongside another item's primary
 * result (e.g. a new-lead deal proposed on an email), so it can be surfaced
 * as its own reviewable card instead of being invisible.
 *
 * @example
 * // Input
 * pickCrmSideEffectResult(
 *   [
 *     { client_item_id: "wf::k::f", draft: "Hi Jane" },
 *     { client_item_id: "wf::k::f:crm", crm_side_effect: true },
 *   ],
 *   "wf::k::f"
 * );
 * // Output
 * { client_item_id: "wf::k::f:crm", crm_side_effect: true }
 */
export const pickCrmSideEffectResult = (results, stableId = "") => {
  const wantedId = String(stableId || "").trim();
  if (!wantedId) return null;
  const rows = Array.isArray(results) ? results.filter((row) => row && typeof row === "object") : [];
  return (
    rows.find(
      (row) => row.crm_side_effect && String(row.client_item_id || "").trim() === `${wantedId}:crm`
    ) || null
  );
};

/**
 * True when live progress has started and no step is still running.
 *
 * @example
 * // Input
 * isPerformerProgressComplete([
 *   { phase: "saving_draft", label: "Output ready — Gmail not connected", status: "done" },
 * ]);
 * // Output
 * true
 */
export const isPerformerProgressComplete = (steps = []) => {
  if (!Array.isArray(steps) || steps.length === 0) return false;
  return !steps.some((step) => String(step?.status || "") === "running");
};

/**
 * Decide how one poll payload should update a single-item performer run.
 *
 * Partial results are applied as soon as they exist so the output panel does
 * not wait for later CRM side-effects (or the terminal "done" status).
 *
 * @example
 * // Input: draft already appended, run still finishing CRM
 * resolveSingleItemPollUpdate({
 *   status: "running",
 *   results: [{ client_item_id: "a", draft: "Hi" }],
 *   stableId: "a",
 *   steps: [{ phase: "saving_draft", status: "done" }],
 * });
 * // Output
 * { applyResult: { client_item_id: "a", draft: "Hi" }, applyCrmSideEffect: null, loading: false, stop: false, pollFast: true, error: false }
 */
export const resolveSingleItemPollUpdate = ({
  status = "",
  results = [],
  stableId = "",
  steps = [],
} = {}) => {
  const rowResult = pickPrimaryPerformerResult(results, stableId);
  const crmSideEffectResult = pickCrmSideEffectResult(results, stableId);
  const runStatus = String(status || "").trim();
  const progressComplete = isPerformerProgressComplete(steps);
  const lastPhase = String(steps?.[steps.length - 1]?.phase || "");
  const savingDraft = lastPhase.startsWith("saving_draft");

  if (runStatus === "error") {
    return {
      applyResult: rowResult,
      applyCrmSideEffect: crmSideEffectResult,
      loading: false,
      stop: true,
      pollFast: false,
      error: true,
    };
  }

  if (rowResult) {
    const done = runStatus === "done";
    return {
      applyResult: rowResult,
      applyCrmSideEffect: crmSideEffectResult,
      loading: false,
      stop: done,
      pollFast: !done,
      error: false,
    };
  }

  if (runStatus === "done") {
    return {
      applyResult: null,
      applyCrmSideEffect: crmSideEffectResult,
      loading: false,
      stop: true,
      pollFast: false,
      error: false,
    };
  }

  return {
    applyResult: null,
    applyCrmSideEffect: crmSideEffectResult,
    loading: true,
    stop: false,
    pollFast: progressComplete || savingDraft,
    error: false,
  };
};

/**
 * Next per-row AI Assist run entry after a state report from a row or the sidebar.
 *
 * The error follows the latest run: a fresh run start, a finished run or a result
 * replaces it (so it never outlives the next sync/run); only an idle update from
 * another surface keeps it.
 *
 * @example
 * nextRowRunEntry({ error: "Couldn't load the source." }, { loading: true, result: null });
 * // { loading: true, progress: { steps: [] }, result: null, crmSideEffect: null, error: "" }
 *
 * @example
 * nextRowRunEntry({ error: "x" }, { loading: false, error: "y", done: true }).error; // "y"
 * nextRowRunEntry({ error: "x" }, { loading: false }).error;                         // "x"
 */
export const nextRowRunEntry = (existing = {}, state = {}) => {
  const startingFreshRun = Boolean(state.loading) && !state.result;
  let nextLoading;
  if (state.loading) {
    nextLoading = true;
  } else if (state.done || state.result || String(state.error || "").trim()) {
    nextLoading = false;
  } else {
    // Idle updates from another surface must not cancel an in-flight run.
    nextLoading = Boolean(existing.loading);
  }
  return {
    loading: nextLoading,
    progress: state.progress?.steps?.length
      ? state.progress
      : nextLoading
        ? existing.progress || { steps: [] }
        : { steps: [] },
    result: startingFreshRun ? null : state.result || existing.result || null,
    crmSideEffect: startingFreshRun
      ? null
      : state.done
        ? state.crmSideEffect || null
        : state.crmSideEffect || existing.crmSideEffect || null,
    error:
      startingFreshRun || state.done || state.result || String(state.error || "").trim()
        ? String(state.error || "")
        : existing.error || "",
  };
};

/**
 * Drop one item's AI Assist error from the per-row run map (user dismissed it).
 * Returns the same map when there is nothing to clear.
 *
 * @example
 * dismissRowRunError({ a: { loading: false, error: "x" } }, "a");
 * // { a: { loading: false, error: "" } }
 */
export const dismissRowRunError = (prev, stableId) => {
  if (!stableId || !prev?.[stableId]?.error) return prev;
  return { ...prev, [stableId]: { ...prev[stableId], error: "" } };
};
